import React from 'react'
import { Sidebar, Heading, Text, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { RoadmapProvider, useRoadmapStore } from '../../stores/projetos/roadmapStore'
import RoadmapFilters from '../../components/projetos/RoadmapFilters'
import RoadmapTimeline from '../../components/projetos/RoadmapTimeline'
import RoadmapKanban from '../../components/projetos/RoadmapKanban'
import { modulesService } from '../../services/modules'
import { Module } from '../../types'

const Content: React.FC<{ module: Module | null }> = ({ module }) => {
  const { view, setView, loadInitial, loading, items } = useRoadmapStore()
  
  React.useEffect(() => { loadInitial() }, [loadInitial])

  // Calcular estatísticas
  const totalItens = items.length
  const itensPorStatus = React.useMemo(() => {
    const statusCount: Record<string, number> = {}
    items.forEach(item => {
      statusCount[item.status] = (statusCount[item.status] || 0) + 1
    })
    return statusCount
  }, [items])

  return (
    <div className="h-full flex flex-col">
              {/* Header */}
              {module && (
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      {module.display_name}
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      {module.description || 'Visões Timeline e Kanban com filtros'}
                    </Text>
                  </div>
                </div>
              )}

      {/* Topo Colorido - Valores */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-x-4 gap-y-2">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
            <div className="text-center">
              <Text size="3xl" className="text-white font-bold mb-2">{totalItens}</Text>
              <Text size="sm" className="text-white">Total de Itens</Text>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg rounded-lg p-6 text-white">
            <div className="text-center">
              <Text size="3xl" className="text-white font-bold mb-2">{itensPorStatus['ideia'] || 0}</Text>
              <Text size="sm" className="text-purple-100">Ideias</Text>
            </div>
          </div>
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-6 text-white">
            <div className="text-center">
              <Text size="3xl" className="text-white font-bold mb-2">{itensPorStatus['planejado'] || 0}</Text>
              <Text size="sm" className="text-yellow-100">Planejados</Text>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
            <div className="text-center">
              <Text size="3xl" className="text-white font-bold mb-2">{itensPorStatus['em_andamento'] || 0}</Text>
              <Text size="sm" className="text-green-100">Em Andamento</Text>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-6 text-white">
            <div className="text-center">
              <Text size="3xl" className="text-white font-bold mb-2">{itensPorStatus['concluido'] || 0}</Text>
              <Text size="sm" className="text-red-100">Concluídos</Text>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="px-6 py-4">
        <RoadmapFilters />
      </div>

      {/* Abas */}
      <div className="px-6">
        <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2">
          <button
            onClick={() => setView('timeline')}
            className={`px-4 py-2 -mb-px border-b-2 ${view === 'timeline' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
          >
            Timeline
          </button>
          <button
            onClick={() => setView('kanban')}
            className={`px-4 py-2 -mb-px border-b-2 ${view === 'kanban' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
          >
            Kanban
          </button>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex-1 p-6 pt-0">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden h-full flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingSpinner size="lg" className="mx-auto" />
            </div>
          ) : (
            <div className="px-4 py-5 sm:p-6 flex-1 flex flex-col overflow-auto">
              {view === 'timeline' ? <RoadmapTimeline /> : <RoadmapKanban />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const ProjetosRoadmapPage: React.FC = () => {
  const [module, setModule] = React.useState<Module | null>(null)
  
  React.useEffect(() => {
    const loadModule = async () => {
      const { data } = await modulesService.getByName('projetos_roadmap')
      if (data) setModule(data)
    }
    loadModule()
  }, [])
  
  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <PermissionGuard 
            moduleName="projetos_roadmap" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
            <RoadmapProvider>
              <Content module={module} />
            </RoadmapProvider>
          </PermissionGuard>
        </main>
      </div>
    </div>
  )
}

export default ProjetosRoadmapPage


